import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { useReveal } from "@/lib/motion";
import { TESTIMONIALS } from "@/lib/siteData";

// Harmoniously spaced post-it coordinates for 6 cards
const POSTIT_LAYOUT = {
    "mylena-pereira": { left: "18%", top: "2%", width: "295px", rotation: -4, z: 2 },
    "marcos-barbosa": { left: "55%", top: "4%", width: "285px", rotation: 6, z: 4 },
    "debora-miranda": { left: "32%", top: "30%", width: "290px", rotation: -3, z: 20 },
    "samir-el-hage": { left: "62%", top: "38%", width: "285px", rotation: -4, z: 5 },
    "renato-andreetta": { left: "8%", top: "48%", width: "285px", rotation: 5, z: 3 },
    "rogerio-venerando": { left: "36%", top: "60%", width: "280px", rotation: -5, z: 2 },
};

export const Testimonials = () => {
    const reveal = useReveal();

    return (
        <section id="depoimentos" className="overflow-hidden bg-paper pb-16 pt-12 md:pb-24 md:pt-16 scroll-mt-[100px]">
            <div className="mx-auto max-w-[1440px] px-6">
                <motion.h2
                    className="type-display mx-auto max-w-[780px] text-center text-[clamp(32px,3.8vw,52px)] font-bold text-brand"
                    {...reveal()}
                >
                    {TESTIMONIALS.headline}
                </motion.h2>

                {/* Desktop Clustered Post-it Collage */}
                <div className="relative mx-auto mt-10 hidden h-[760px] w-full max-w-[1000px] md:block">
                    {TESTIMONIALS.cards.map((card, index) => {
                        const layout = POSTIT_LAYOUT[card.id] || {
                            left: `${(index % 3) * 33}%`,
                            top: `${Math.floor(index / 3) * 40}%`,
                            width: "280px",
                            rotation: 0,
                            z: index,
                        };

                        return (
                            <motion.div
                                key={card.id || card.company}
                                className="absolute cursor-pointer"
                                style={{
                                    left: layout.left,
                                    top: layout.top,
                                    width: layout.width,
                                    zIndex: layout.z,
                                }}
                                initial={{ opacity: 0, scale: 0.85, rotate: layout.rotation }}
                                whileInView={{ opacity: 1, scale: 1, rotate: layout.rotation }}
                                viewport={{ once: true, margin: "-50px" }}
                                transition={{ duration: 0.45, delay: index * 0.05 }}
                                whileHover={{
                                    scale: 1.09,
                                    rotate: 0,
                                    zIndex: 50,
                                    transition: { duration: 0.2, ease: "easeOut" },
                                }}
                            >
                                <PostItCard card={card} />
                            </motion.div>
                        );
                    })}
                </div>

                {/* Mobile / Tablet Stack */}
                <div className="mt-8 grid grid-cols-1 gap-5 sm:grid-cols-2 md:hidden">
                    {TESTIMONIALS.cards.map((card, index) => {
                        const layout = POSTIT_LAYOUT[index] || { rotation: (index % 2 === 0 ? -3 : 3) };
                        return (
                            <motion.div
                                key={card.id || card.company}
                                className="cursor-pointer"
                                initial={{ opacity: 0, y: 15 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                viewport={{ once: true }}
                                transition={{ duration: 0.35, delay: index * 0.04 }}
                                whileHover={{
                                    scale: 1.03,
                                    rotate: 0,
                                    transition: { duration: 0.2 },
                                }}
                                style={{ transform: `rotate(${layout.rotation * 0.4}deg)` }}
                            >
                                <PostItCard card={card} />
                            </motion.div>
                        );
                    })}
                </div>
            </div>
        </section>
    );
};

const YellowStars = ({ rating = 5 }) => (
    <div className="flex items-center gap-0.5" aria-label={`${rating} estrelas`}>
        {Array.from({ length: 5 }).map((_, i) => {
            const isFilled = i < rating;
            return (
                <svg
                    key={i}
                    viewBox="0 0 20 20"
                    fill={isFilled ? "#FFC700" : "rgba(255,255,255,0.45)"}
                    className="h-[14px] w-[14px] drop-shadow-[0_1px_1px_rgba(0,0,0,0.15)]"
                    aria-hidden="true"
                >
                    <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                </svg>
            );
        })}
    </div>
);

const PostItCard = ({ card }) => {
    return (
        <Link
            to={card.href}
            aria-label={`${card.person} — ${card.role || card.company}`}
            className="group flex flex-col justify-between rounded-[22px] p-5 text-white shadow-lg transition-shadow duration-300 hover:shadow-2xl"
            style={{ backgroundColor: card.color || "#EA6D11" }}
        >
            <div>
                {/* Yellow Stars (with 4 or 5 rating) */}
                <YellowStars rating={card.rating || 5} />

                {/* Testimonial Quote */}
                <div className="mt-3 space-y-2 text-[12.5px] font-normal leading-[1.36] text-white sm:text-[13px]">
                    {card.quote.map((paragraph, pIdx) => (
                        <p key={pIdx} className="leading-[1.36]">
                            {Array.isArray(paragraph) ? (
                                paragraph.map((run, rIdx) =>
                                    run.bold ? (
                                        <strong key={rIdx} className="font-bold text-white">
                                            {run.text}
                                        </strong>
                                    ) : (
                                        <span key={rIdx}>{run.text}</span>
                                    )
                                )
                            ) : typeof paragraph === "string" ? (
                                paragraph
                            ) : paragraph.bold ? (
                                <strong className="font-bold text-white">{paragraph.text}</strong>
                            ) : (
                                <span>{paragraph.text}</span>
                            )}
                        </p>
                    ))}
                </div>
            </div>

            {/* Author Footer */}
            <div className="mt-4 flex items-center gap-2.5 border-t border-white/15 pt-2.5">
                <div className="h-6.5 w-6.5 shrink-0 rounded-full bg-white shadow-sm" />
                <div className="min-w-0">
                    <p className="type-display text-[14px] font-bold leading-tight text-white">
                        {card.person}
                    </p>
                    <p className="truncate text-[11px] font-light leading-tight text-white/85">
                        {card.role}
                    </p>
                </div>
            </div>
        </Link>
    );
};
